import { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell, Banner, Button, EmptyState, Skeleton, Tabs, TopBar, useToast } from "@guestpilot/ui-kit";
import { pages } from "@microsoft/teams-js";
import { useGet, useWrite } from "../../api/hooks";
import type { Team } from "../../api/types";
import { ENV } from "../../env";
import { useHandleApiError } from "../../errors";
import { useI18n } from "../../i18n";
import { StateBanner } from "../AccountState";
import { useSession } from "../../session";
import type { TeamsInfo } from "../../teams";
import { History } from "./History";
import { Notifications } from "../Notifications";
import { useInbox } from "../../lib/inbox";
import { routeFor } from "../../lib/pages";
import { Overview } from "./Overview";
import { Pending } from "./Pending";
import { Review } from "./Review";

/** Configuration page of the tab (Teams `settings` frame): the owner adds GuestPilot to a Team and saves; removing the tab needs no handler (BR-021, BR-272). */
function ConfigPage() {
  const { t } = useI18n();
  useEffect(() => {
    if (ENV.mock) return;
    void (async () => {
      pages.config.registerOnSaveHandler((event) => {
        void pages.config
          .setConfig({
            entityId: "team-overview",
            contentUrl: `${window.location.origin}/`,
            websiteUrl: `${window.location.origin}/`,
            suggestedDisplayName: "GuestPilot",
          })
          .then(() => event.notifySuccess())
          .catch((e: unknown) => event.notifyFailure(String(e)));
      });
      pages.config.registerOnRemoveHandler((event) => event.notifySuccess());
      pages.config.setValidityState(true);
    })();
  }, []);
  return (
    <div className="gp-state-page">
      <div className="gp-state-card">
        <h1 className="t-title-lg" style={{ margin: 0 }}>
          {t("ui:config.title")}
        </h1>
        <p className="t-body-lg gp-muted" style={{ margin: 0 }}>
          {t("ui:config.body")}
        </p>
      </div>
    </div>
  );
}

/**
 * GuestPilot is installed in the Team but the Team is not active (deactivated by an Admin or an Owner, or waiting for a free slot
 * of the plan): say so, and let whoever may activate it do it from here (BR-272, BR-232).
 */
function InactiveTeam({ team }: { team: Team }) {
  const { t } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const activate = useWrite("POST", "/api/v1/teams/:id/activate", [
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/subscription",
    "/api/v1/activation",
  ]);
  const waiting = team.status === "blocked_limit";
  return (
    <div className="gp-state-page">
      <div className="gp-state-card">
        <EmptyState title={waiting ? t("ui:tab.blocked") : t("ui:tab.deactivated")} />
        <Button
          variant="primary"
          disabledReason={
            s.gate("TEAM_ACTIVATE", team.id) ?? (activate.isPending ? t("common:loading") : undefined)
          }
          onClick={() =>
            activate.mutate(
              { params: { id: team.id } },
              { onSuccess: () => toast.success(t("ui:tab.reactivated")), onError: fail },
            )
          }
        >
          {waiting ? t("ui:tab.retry") : t("ui:tab.reactivate")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The app runs in a Team GuestPilot does not know yet (D-148: nothing tells the API when the app is added). The tab asks the API to
 * activate it once; the API checks through Graph that the person owns the Team (or is an Admin in it). A refusal shows the usual
 * "not active yet" message, with a retry for a limit or a passing error.
 */
function NewTeam({ graphTeamId }: { graphTeamId: string }) {
  const { t } = useI18n();
  const asked = useRef(false);
  const activate = useWrite("POST", "/api/v1/teams/activate-from-tab", [
    "/api/v1/teams",
    "/api/v1/dashboard",
    "/api/v1/subscription",
    "/api/v1/activation",
  ]);
  const { mutate } = activate;
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    mutate({ body: { graphTeamId } });
  }, [graphTeamId, mutate]);
  if (activate.isError)
    return (
      <div className="gp-state-page">
        <div className="gp-state-card">
          <EmptyState title={t("ui:tab.notActive")} />
          <Button variant="primary" onClick={() => activate.mutate({ body: { graphTeamId } })}>
            {t("ui:tab.retry")}
          </Button>
        </div>
      </div>
    );
  return <Skeleton rows={6} />;
}

/**
 * The Team tab and, for a person who only has Team roles (Owner, Delegate), the personal app: scoped to one Team.
 * The Team comes from the Teams context (never from the URL); a deep link carries the Team id as sub-page (team-tab.md).
 */
export function TeamTab({ teams }: { teams: TeamsInfo }) {
  const { t } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const list = useGet<Team[]>("/api/v1/teams", { filter: "all" });
  const inbox = useInbox();
  const [picked, setPicked] = useState<string | undefined>(
    () => /^\/review\/[\w-]+\/([\w-]+)/.exec(loc.pathname)?.[1],
  );
  // a deep link carries the page to open (D-143): it lands on the closest page of the Team tab, once, at start
  useEffect(() => {
    if (teams.subPageId?.startsWith("/")) nav(routeFor(teams.subPageId, true), { replace: true });
  }, []);
  if (teams.frame === "config") return <ConfigPage />;
  if (list.isLoading) return <Skeleton rows={6} />;
  const mine = (list.data ?? []).filter((x) => x.status === "active");
  // a review notice names its Team in the path (/review/<campaign>/<team>): it wins over the Team of the context
  const pathTeam = /^\/review\/[\w-]+\/([\w-]+)/.exec(loc.pathname)?.[1];
  const team =
    mine.find((x) => x.id === picked) ??
    mine.find((x) => x.id === pathTeam) ??
    (teams.groupId ? mine.find((x) => x.graphTeamId === teams.groupId) : undefined) ??
    mine.find((x) => x.id === teams.subPageId) ??
    (teams.frame === "personal" ? mine[0] : undefined);

  if (!team) {
    // the Team is known but not active: it is not "to be added from the store", it is installed and can be reactivated
    const known = teams.groupId
      ? (list.data ?? []).find((x) => x.graphTeamId === teams.groupId && x.status !== "team_unavailable")
      : undefined;
    if (known) return <InactiveTeam team={known} />;
    if (teams.groupId) return <NewTeam graphTeamId={teams.groupId} />;
    return (
      <div className="gp-state-page">
        <EmptyState title={teams.groupId ? t("ui:tab.notActive") : t("ui:tab.none")} />
      </div>
    );
  }

  const path = loc.pathname.startsWith("/review")
    ? "review"
    : loc.pathname.startsWith("/pending")
      ? "pending"
      : loc.pathname.startsWith("/history")
        ? "history"
        : loc.pathname.startsWith("/notifications")
          ? "notifications"
          : "overview";
  const owner = (team.owners ?? []).map((o) => o.displayName).join(", ");
  return (
    <AppShell
      tab
      topBar={
        <TopBar
          left={
            <div className="gp-person-text">
              <span className="t-title-sm">
                {team.displayName} · {t("common:appName")}
              </span>
              <span className="t-body-sm gp-muted">
                {t("ui:tab.sub", { n: team.externalUserCount ?? 0, owner })}
              </span>
            </div>
          }
          person={{ name: s.me.person.displayName }}
        />
      }
    >
      <div className="gp-stack" style={{ gap: "var(--space-16)" }}>
        <StateBanner />
        {teams.frame === "personal" && mine.length > 1 ? (
          <select
            className="gp-select"
            aria-label={t("ui:tab.switch")}
            value={team.id}
            onChange={(e) => setPicked(e.target.value)}
          >
            {mine.map((x) => (
              <option key={x.id} value={x.id}>
                {x.displayName}
              </option>
            ))}
          </select>
        ) : null}
        {team.status !== "active" ? <Banner tone="warn">{t(`ui:teamStatus.${team.status}`)}</Banner> : null}
        <Tabs
          label={t("ui:tab.nav")}
          value={path}
          onChange={(k) =>
            nav(
              k === "overview"
                ? "/"
                : k === "review"
                  ? team.activeCampaignId
                    ? `/review/${team.activeCampaignId}`
                    : "/"
                  : `/${k}`,
            )
          }
          tabs={[
            { key: "overview", label: t("ui:tab.overview") },
            ...(team.activeCampaignId ? [{ key: "review", label: t("ui:tab.review") }] : []),
            { key: "pending", label: t("ui:tab.pending") },
            { key: "history", label: t("ui:tab.history") },
            { key: "notifications", label: t("ui:nav.notifications"), count: inbox.data?.unread ?? 0 },
          ]}
        />
        <Routes>
          <Route index element={<Overview team={team} />} />
          <Route path="review/:campaignId" element={<Review teamId={team.id} />} />
          <Route path="review/:campaignId/:teamId" element={<Review teamId={team.id} />} />
          <Route path="pending" element={<Pending team={team} />} />
          <Route path="history" element={<History team={team} />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </AppShell>
  );
}
